import {
  Handshake,
  DesignServices,
  RequestQuote,
  SettingsSuggest,
  Code,
  Build,
  Backup,
  Security,
  EmojiEvents,
} from "@mui/icons-material";

import { Box, Card, Typography } from "@mui/material";
import { useContext } from "react";
import { LangContext } from "../App";
import { motion } from "framer-motion";
import { textVariants } from "../components/textVariants";
import manager from "../images/manager.jpg";
const steps_pl = [
  {
    title: "Współpraca",
    icon: <Handshake />,
    description:
      "Rozmawiamy o potrzebach, celach biznesowych i problemach do rozwiązania.",
  },
  {
    title: "Projekt",
    icon: <DesignServices />,
    description:
      "Proponuję koncepcję rozwiązania i sposób działania aplikacji.",
  },
  {
    title: "Wycena",
    icon: <RequestQuote />,
    description:
      "Przedstawiam jasną wycenę i zakres prac – bez ukrytych kosztów.",
  },
  {
    title: "Dobór technologii",
    icon: <SettingsSuggest />,
    description:
      "Dobieram technologie dopasowane do skali projektu i przyszłego rozwoju.",
  },
  {
    title: "Wykonanie",
    icon: <Code />,
    description: "Tworzę aplikację etapami, z bieżącym podglądem postępów.",
  },
  {
    title: "Utrzymanie",
    icon: <Build />,
    description:
      "Zapewniam stabilne działanie aplikacji, aktualizacje i bieżące wsparcie.",
  },
  {
    title: "Backup danych",
    icon: <Backup />,
    description:
      "Konfiguruję regularne kopie zapasowe, aby dane były bezpieczne.",
  },
  {
    title: "Bezpieczeństwo",
    icon: <Security />,
    description:
      "Dbam o ochronę danych, autoryzację użytkowników i zabezpieczenia.",
  },
  {
    title: "Sukces",
    icon: <EmojiEvents />,
    description:
      "Gotowe rozwiązanie działa stabilnie, rozwija się i realnie wspiera biznes.",
    isSuccess: true,
  },
];

const steps_en = [
  {
    title: "Consultation",
    icon: <Handshake />,
    description:
      "We discuss your requirements, business goals, and the challenges to be solved.",
  },
  {
    title: "Solution Design",
    icon: <DesignServices />,
    description:
      "I propose a solution concept and define how the application will work.",
  },
  {
    title: "Project Estimate",
    icon: <RequestQuote />,
    description:
      "I provide a clear project estimate and scope of work — with no hidden costs.",
  },
  {
    title: "Technology Selection",
    icon: <SettingsSuggest />,
    description:
      "I choose technologies that fit the project's scale and support future growth.",
  },
  {
    title: "Development",
    icon: <Code />,
    description:
      "I build the application in stages, keeping you updated on progress throughout the process.",
  },
  {
    title: "Maintenance",
    icon: <Build />,
    description:
      "I ensure reliable operation, regular updates, and ongoing support.",
  },
  {
    title: "Data Backup",
    icon: <Backup />,
    description:
      "I configure regular backups to keep your data secure and recoverable.",
  },
  {
    title: "Security",
    icon: <Security />,
    description:
      "I implement data protection, user authentication, and security best practices.",
  },
  {
    title: "Success",
    icon: <EmojiEvents />,
    description:
      "The finished solution runs reliably, evolves with your needs, and delivers real business value.",
    isSuccess: true,
  },
];

export default function CooperationFlow() {
  const language = useContext(LangContext);
  const steps = language === "pl" ? steps_pl : steps_en;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={textVariants}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 5, md: 8 },
          boxSizing: "border-box",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            maxWidth: 1000,
            mx: "auto",
            mb: 6,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 3,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          {/* ZDJĘCIE MANAGERA */}
          <Box
            component="img"
            src={manager}
            alt="Business manager"
            sx={{
              width: { xs: 180, md: 230 },
              height: { xs: 130, md: 180 },
              objectFit: "cover",
              borderRadius: 3,
              flexShrink: 0,
              boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
            }}
          />

          {/* TEKST */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: 17, md: 20 },
                fontWeight: 700,
                color: "#111827",
                mb: 2,
              }}
            >
              {language === "pl"
                ? "Jak wygląda współpraca?"
                : "How does the collaboration work?"}
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: 16, md: 18 },
                color: "#4b5563",
                lineHeight: 1.6,
              }}
            >
              {language === "pl"
                ? "Od analizy wymagań po gotowe wdrożenie – Twój projekt będzie prowadzonyprzez cały proces tworzenia aplikacji, dbając o jakość, bezpieczeństwo i cele biznesowe."
                : "From requirements analysis to final deployment – I guide the project through the entire application development process, ensuring quality, security, and alignment with business goals."}
            </Typography>
          </Box>
        </Box>

        {/* COOPERATION STEPS */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          {steps.map((step, index) => (
            <Card
              key={index}
              elevation={0}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 5px 16px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    color: "#ffffff",
                    backgroundColor: step.isSuccess ? "#059669" : "#1E3A5F",
                  }}
                >
                  {step.icon}
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: 17, md: 20 },
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {step.title}
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 32, md: 20 },
                  color: "#4b5563",
                  lineHeight: 1.7,
                }}
              >
                {step.description}
              </Typography>
            </Card>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
}
